import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccessibilitySettings = ({ user, userProfile, onSuccess }) => {
  const [settings, setSettings] = useState({
    fontSize: 'medium',
    colorContrast: 'normal',
    keyboardNavigation: true,
    screenReader: false,
    reducedMotion: false
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleFontSizeChange = (size) => {
    setSettings(prev => ({ ...prev, fontSize: size }));
  };

  const handleContrastChange = (contrast) => {
    setSettings(prev => ({ ...prev, colorContrast: contrast }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      onSuccess?.('Accessibility settings updated successfully!');
    }, 1000);
  };

  const fontSizes = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'extra-large', label: 'Extra Large' }
  ];

  const contrastOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High Contrast' },
    { value: 'dark', label: 'Dark Mode' }
  ];

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Accessibility Settings
        </h2>
        <p className="text-blue-100">
          Customize display and navigation options
        </p>
      </div>

      {/* Font Size */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Type" size={24} className="text-blue-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Font Size</h3>
            <p className="text-blue-200 text-sm">Adjust text size for better readability</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {fontSizes?.map((size) => (
            <button
              key={size?.value}
              onClick={() => handleFontSizeChange(size?.value)}
              className={`px-4 py-3 rounded-xl transition-all ${
                settings?.fontSize === size?.value
                  ? 'bg-purple-600 text-white' :'bg-white bg-opacity-10 text-blue-100 hover:bg-opacity-20'
              }`}
            >
              <span className="font-medium">{size?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Color Contrast */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Contrast" size={24} className="text-purple-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Color Contrast</h3>
            <p className="text-blue-200 text-sm">Choose your preferred color scheme</p>
          </div>
        </div>

        <div className="space-y-2">
          {contrastOptions?.map((option) => (
            <button
              key={option?.value}
              onClick={() => handleContrastChange(option?.value)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                settings?.colorContrast === option?.value
                  ? 'bg-purple-600 text-white' :'bg-white bg-opacity-10 text-blue-100 hover:bg-opacity-20'
              }`}
            >
              <span className="font-medium">{option?.label}</span>
              {settings?.colorContrast === option?.value && (
                <Icon name="Check" size={20} />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Navigation Options */}
      <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
        <div className="flex items-center space-x-3">
          <Icon name="Navigation" size={24} className="text-green-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Navigation Options</h3>
            <p className="text-blue-200 text-sm">Enhance navigation accessibility</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
            <div>
              <p className="text-white font-medium">Keyboard Navigation</p>
              <p className="text-blue-200 text-sm">Enable keyboard shortcuts</p>
            </div>
            <button
              onClick={() => handleToggle('keyboardNavigation')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.keyboardNavigation ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.keyboardNavigation ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
            <div>
              <p className="text-white font-medium">Screen Reader Support</p>
              <p className="text-blue-200 text-sm">Optimize for screen readers</p>
            </div>
            <button
              onClick={() => handleToggle('screenReader')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.screenReader ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.screenReader ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-white font-medium">Reduced Motion</p>
              <p className="text-blue-200 text-sm">Minimize animations and transitions</p>
            </div>
            <button
              onClick={() => handleToggle('reducedMotion')}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                settings?.reducedMotion ? 'bg-purple-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  settings?.reducedMotion ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={isSaving}
          iconName="Save"
          iconPosition="left"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AccessibilitySettings;